// src/components/payroll/PayslipModal.js — Phase 4a
// Individual payslip preview modal with PDF download.

import { Fragment, useId, useState } from 'react';
import Modal from '../modals/Modal';
import {
  buildEarningsRows,
  buildDeductionRows,
  buildAdditionalEarnings,
  DEDUCTION_GROUPS,
  groupOf,
  getPayslipEmployeeName,
  formatNum,
} from '../../utils/payslipBuilders';
import { calculateEmployeeFinancials } from '../../utils/payrollCalculations';
import { loadPayslipPdf, PDF_LOAD_ERROR } from '../../services/pdfLoader';
import { useToast } from '../../contexts/ToastContext';

// ── Formatters ────────────────────────────────────────────────

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  })
    .format(amount || 0)
    .replace('PHP', '₱');
}

function formatPeriod(start, end) {
  const opts = { month: 'short', day: 'numeric' };
  const startStr = new Intl.DateTimeFormat('en-US', opts).format(start);
  const endStr = new Intl.DateTimeFormat('en-US', opts).format(end);
  return `${startStr} – ${endStr}, ${end.getFullYear()}`;
}

function getSalaryRateLabel(emp) {
  const type = emp.employment?.salaryType;
  if (type === 'daily') {
    return {
      typeLabel: 'Daily Rate',
      rateLabel: formatCurrency(emp.employment?.dailySalary),
    };
  }
  if (type === 'monthly') {
    return {
      typeLabel: 'Monthly Rate',
      rateLabel: formatCurrency(emp.employment?.monthlySalary),
    };
  }
  return { typeLabel: type || '—', rateLabel: '—' };
}

// ── Component ─────────────────────────────────────────────────

const PayslipModal = ({ employee, run, company, onClose, inkFriendly = false }) => {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const titleId = useId();

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    let generatePayslipPdf;
    try {
      generatePayslipPdf = await loadPayslipPdf();
    } catch (err) {
      console.error('[PayslipModal] PDF load error:', err);
      toast.error(PDF_LOAD_ERROR);
      setDownloading(false);
      return;
    }
    try {
      generatePayslipPdf(employee, run, company, inkFriendly);
    } catch (err) {
      console.error('[PayslipModal] PDF error:', err);
      toast.error('Failed to generate the payslip PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  // Esc and the body scroll lock now come from the shared <Modal> shell.

  if (!employee || !run) return null;

  const name = getPayslipEmployeeName(employee);
  const displayRunId = run.runId || run.id;
  const period = formatPeriod(run.periodStart, run.periodEnd);
  const { typeLabel, rateLabel } = getSalaryRateLabel(employee);
  // Top-level first (legacy + fixed server runs); fall back to nested
  // pii.governmentIds saved by pre-fix server-compute runs.
  const govIds = employee.governmentIds || employee.pii?.governmentIds || {};

  const earningsRows = buildEarningsRows(employee, run.ratesUsed);
  const deductionRows = buildDeductionRows(employee, run.periodStart, run.periodEnd);

  const additional = buildAdditionalEarnings(employee);

  // PRD_PayrollFigureAlignment C1/C2/C5 — the totals come from the shared module,
  // never from summing the rows above. Deriving them here is what let the two
  // payslips disagree: whatever got folded into a row silently moved Gross, which
  // is how one run printed Gross ₱1,890.63 here and ₱1,390.63 on the bulk slip
  // (BL-048).
  // Invariants 1 and 3 (payslipBuilders.test.js) assert the rows foot to these
  // figures. Known exception: a run saved before government deductions moved to
  // per-month maps renders no deduction rows at all in a two-month period, because
  // buildDeductionRows walks months and a flat legacy value belongs to none of
  // them, while totalWithheld sums every key. Legacy data only — nothing writes
  // that shape now — and deliberately left alone, since the fix would touch the
  // SSS path this PRD froze.
  const fin = calculateEmployeeFinancials(employee);
  const gross = fin.gross;
  const totalDeductions = fin.totalWithheld;
  const netPay = fin.net;

  // busy stays false: closing mid-download is harmless — the PDF still saves.
  return (
    <Modal isOpen onClose={onClose} labelledBy={titleId} className="psm-modal">

        {/* ── Sticky Header (Download stays here; the × is the shell's) ── */}
        <div className="psm-header">
          <div className="psm-header__left">
            <div className="psm-header__title" id={titleId}>Payslip — {name}</div>
            <div className="psm-header__subtitle">Run {displayRunId} · {period}</div>
          </div>
          <div className="psm-header__actions">
            <button className="psm-btn-download" onClick={handleDownload} disabled={downloading}>
              {downloading ? 'Preparing…' : '↓ Download'}
            </button>
          </div>
        </div>

        {/* ── Scrollable Body ── */}
        <div className="psm-body">
          <div className="psm-doc">

            {/* Company Header */}
            <div className="psm-company-header">
              <div className="psm-company-header__left">
                <div className="psm-company-name">{company?.name || '—'}</div>
                {company?.address && (
                  <div className="psm-company-address">{company.address}</div>
                )}
              </div>
              <div className="psm-company-header__right">
                <div className="psm-payslip-title">PAYSLIP</div>
                <div className="psm-run-id">{displayRunId}</div>
                <div className="psm-run-period">{period}</div>
              </div>
            </div>

            {/* Employee Information */}
            <div className="psm-section">
              <div className="psm-section-label">EMPLOYEE INFORMATION</div>
              <div className="psm-emp-grid">
                <div className="psm-emp-field">
                  <span className="psm-emp-field__label">Full Name</span>
                  <span className="psm-emp-field__value">{name}</span>
                </div>
                <div className="psm-emp-field">
                  <span className="psm-emp-field__label">Employee ID</span>
                  <span className="psm-emp-field__value psm-mono">
                    {employee.employeeId || '—'}
                  </span>
                </div>
                <div className="psm-emp-field">
                  <span className="psm-emp-field__label">Department</span>
                  <span className="psm-emp-field__value">
                    {employee.employment?.department || '—'}
                  </span>
                </div>
                <div className="psm-emp-field">
                  <span className="psm-emp-field__label">Salary Type</span>
                  <span className="psm-emp-field__value">{typeLabel}</span>
                </div>
                <div className="psm-emp-field">
                  <span className="psm-emp-field__label">Rate</span>
                  <span className="psm-emp-field__value psm-mono">{rateLabel}</span>
                </div>
              </div>
            </div>

            {/* Government IDs */}
            <div className="psm-gov-ids">
              <div className="psm-gov-id">
                <span className="psm-gov-id__label">SSS</span>
                <span className="psm-gov-id__value">{govIds.sss || '—'}</span>
              </div>
              <div className="psm-gov-id">
                <span className="psm-gov-id__label">PhilHealth</span>
                <span className="psm-gov-id__value">{govIds.philhealth || '—'}</span>
              </div>
              <div className="psm-gov-id">
                <span className="psm-gov-id__label">Pag-IBIG</span>
                <span className="psm-gov-id__value">{govIds.pagibig || '—'}</span>
              </div>
              <div className="psm-gov-id">
                <span className="psm-gov-id__label">TIN</span>
                <span className="psm-gov-id__value">{govIds.tin || '—'}</span>
              </div>
            </div>

            {/* Earnings Table */}
            <div className="psm-section">
              <div className="psm-section-label">EARNINGS</div>
              <table className="psm-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="psm-th-num">Days / Hours</th>
                    <th className="psm-th-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsRows.map((row, i) => (
                    <tr key={i} className={row.isNegative ? 'psm-row--negative' : ''}>
                      <td>
                        {row.label}
                        {row.subLabel && (
                          <span className="psm-row-sublabel"> {row.subLabel}</span>
                        )}
                      </td>
                      <td className="psm-td-num">
                        {row.quantity != null
                          ? `${formatNum(row.quantity)} ${row.unit || ''}`
                          : '—'}
                      </td>
                      <td className="psm-td-num psm-td-amount">
                        {row.isNegative
                          ? `–${formatCurrency(Math.abs(row.amount))}`
                          : formatCurrency(row.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="psm-subtotal psm-subtotal--gross">
                <span>Gross Pay</span>
                <span>{formatCurrency(gross)}</span>
              </div>
            </div>

            {/* Deductions Table */}
            <div className="psm-section">
              <div className="psm-section-label">DEDUCTIONS</div>
              <table className="psm-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="psm-th-num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {/* C2/BL-088 — grouped under headings so a "Cash advance" can never
                      read as a government contribution. C12: a group with no rows
                      renders no heading. Row order within the list is unchanged. */}
                  {DEDUCTION_GROUPS.map(({ key, heading }) => {
                    const rows = deductionRows.filter((r) => groupOf(r) === key);
                    if (rows.length === 0) return null;
                    return (
                      <Fragment key={key}>
                        <tr className="psm-group-row">
                          <td colSpan={2} className="psm-group-heading">{heading}</td>
                        </tr>
                        {rows.map((row, i) => (
                          <tr key={`${key}-${i}`}>
                            <td className="psm-grouped-label">{row.label}</td>
                            <td className="psm-td-num psm-td-amount">
                              {formatCurrency(row.thisPeriod)}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div className="psm-subtotal psm-subtotal--deduction">
                <span>Total Deductions</span>
                <span>{totalDeductions > 0 ? `–${formatCurrency(totalDeductions)}` : formatCurrency(0)}</span>
              </div>
            </div>

            {/* Additional Earnings — manual earning adjustments (C4). They sit
                OUTSIDE Gross, between Total Deductions and Net Pay. C12: hidden
                entirely when there are none, which is most payslips. */}
            {additional.rows.length > 0 && (
              <div className="psm-section">
                <div className="psm-section-label">ADDITIONAL EARNINGS</div>
                <table className="psm-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th className="psm-th-num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additional.rows.map((row, i) => (
                      <tr key={i}>
                        <td>{row.label}</td>
                        <td className="psm-td-num psm-td-amount">
                          {formatCurrency(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="psm-subtotal">
                  <span>Total Additional Earnings</span>
                  <span>{formatCurrency(additional.total)}</span>
                </div>
              </div>
            )}

            {/* Net Pay */}
            <div className="psm-net-pay">
              <span className="psm-net-pay__label">NET PAY</span>
              <span className="psm-net-pay__value">{formatCurrency(netPay)}</span>
            </div>

            {/* Confidentiality Notice */}
            <div className="psm-confidential">
              This payslip is confidential and intended solely for the named employee.
              Unauthorized disclosure is prohibited.
            </div>

          </div>
        </div>

        {/* ── Sticky Footer ── */}
        <div className="psm-footer">
          <div className="psm-footer__id">
            {name} · #{employee.employeeId || '—'} · Run {displayRunId}
          </div>
          <div className="psm-footer__actions">
            <button className="psm-footer__btn-close" onClick={onClose}>Close</button>
            <button className="psm-footer__btn-download" onClick={handleDownload} disabled={downloading}>
              {downloading ? 'Preparing…' : '↓ Download'}
            </button>
          </div>
        </div>

    </Modal>
  );
};

export default PayslipModal;
